import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface DownloadJob {
  jobId: string
  status: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed'
  progress: number
  downloadUrl: string | null
  fileName: string | null
  error: string | null
  totalExpenses: number | null
  processedExpenses: number | null
  currentPhase: string | null
  startedAt: number
  jobType?: 'expenses' | 'reconciliation' // Optional: defaults to 'expenses' for backwards compatibility
}

interface DownloadJobsState {
  activeJobs: DownloadJob[]
}

export const DOWNLOAD_JOBS_STORAGE_KEY = 'downloadJobs'

/**
 * Rehydrates active download jobs from sessionStorage so an in-flight download
 * survives a page reload (the server-side job keeps running and the overlay's
 * poller resumes). sessionStorage is per-tab and clears when the tab closes.
 */
const getInitialState = (): DownloadJobsState => {
  try {
    const raw = sessionStorage.getItem(DOWNLOAD_JOBS_STORAGE_KEY)
    if (!raw) return { activeJobs: [] }
    const parsed = JSON.parse(raw) as DownloadJobsState
    if (!parsed || !Array.isArray(parsed.activeJobs)) return { activeJobs: [] }
    return { activeJobs: parsed.activeJobs }
  } catch {
    return { activeJobs: [] }
  }
}

const initialState: DownloadJobsState = getInitialState()

export const downloadJobs = createSlice({
  name: 'downloadJobs',
  initialState,
  reducers: {
    addJob: (state, action: PayloadAction<DownloadJob>) => {
      state.activeJobs.push(action.payload)
    },
    updateJob: (state, action: PayloadAction<Partial<DownloadJob> & { jobId: string }>) => {
      const idx = state.activeJobs.findIndex((j) => j.jobId === action.payload.jobId)
      if (idx !== -1) {
        state.activeJobs[idx] = { ...state.activeJobs[idx], ...action.payload }
      }
    },
    removeJob: (state, action: PayloadAction<string>) => {
      state.activeJobs = state.activeJobs.filter((j) => j.jobId !== action.payload)
    }
  }
})

export default downloadJobs.reducer
